import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';

const EMPTY_SET = new Set<number>();

/**
 * Hook that detects when tabs overflow their container and determines
 * which tabs should be hidden.
 *
 * @param selectedTabIndex - The index of the currently selected tab, which is always kept visible
 * @returns An object containing:
 *   - `tabListRef` - Ref to attach to the wrapper element around the tab list
 *   - `moreButtonRef` - Ref to attach to the "More" button element, used to calculate its width
 *   - `hasOverflow` - Whether the total width of the tabs exceeds the container width
 *   - `hiddenTabIndices` - A Set of tab indices that should be hidden because they don't fit
 */
export function useOverflowTabs(selectedTabIndex: number): {
	tabListRef: MutableRefObject<HTMLDivElement | null>;
	moreButtonRef: MutableRefObject<HTMLDivElement | null>;
	hasOverflow: boolean;
	hiddenTabIndices: Set<number>;
} {
	const tabListContainerRef = useRef<HTMLDivElement | null>(null);
	const moreButtonRef = useRef<HTMLDivElement | null>(null);
	const [hasOverflow, setHasOverflow] = useState(false);
	const [hiddenTabIndices, setHiddenTabIndices] = useState<Set<number>>(EMPTY_SET);

	const tabWidthsRef = useRef<number[]>([]);

	const checkOverflow = useCallback(() => {
		const containerEl = tabListContainerRef.current;
		if (!containerEl) {
			return;
		}

		const tabs = containerEl.querySelectorAll('[role="tab"]');
		if (tabs.length === 0) {
			setHasOverflow(false);
			setHiddenTabIndices(EMPTY_SET);
			return;
		}

		// Capture tab widths on the first measurement (before any are hidden).
		// Use scrollWidth to get the natural (untruncated) width of each tab.
		if (tabWidthsRef.current.length === 0) {
			tabWidthsRef.current = Array.from(tabs).map((tab) => tab.scrollWidth);
		}

		const tabWidths = tabWidthsRef.current;

		const totalTabWidth = tabWidths.reduce((sum, w) => sum + w, 0);
		const containerWidth = containerEl.clientWidth;

		if (totalTabWidth <= containerWidth) {
			setHasOverflow(false);
			setHiddenTabIndices(EMPTY_SET);
			return;
		}

		setHasOverflow(true);

		const moreButtonWidth = moreButtonRef.current?.getBoundingClientRect().width ?? 0;

		// Always show the selected tab (it may truncate)
		const selectedTabWidth = tabWidths[selectedTabIndex] ?? 0;
		let remainingWidth = containerWidth - selectedTabWidth - moreButtonWidth;
		const hidden = new Set<number>();

		for (let i = 0; i < tabWidths.length; i++) {
			if (i === selectedTabIndex) {
				continue;
			}
			// Only show a non-selected tab if its full width fits
			if (tabWidths[i] <= remainingWidth) {
				remainingWidth -= tabWidths[i];
			} else {
				hidden.add(i);
			}
		}

		setHiddenTabIndices(hidden);
	}, [selectedTabIndex]);

	useEffect(() => {
		const containerEl = tabListContainerRef.current;
		if (!containerEl) {
			return;
		}

		const observer = new ResizeObserver(() => {
			tabWidthsRef.current = [];
			checkOverflow();
		});

		observer.observe(containerEl);

		return () => {
			observer.disconnect();
		};
	}, [checkOverflow]);

	return {
		tabListRef: tabListContainerRef,
		moreButtonRef,
		hasOverflow,
		hiddenTabIndices,
	};
}

/**
 * A no-op version of useOverflowTabs for when the responsive tabs feature gate is disabled.
 * Returns refs that are never attached to anything and always reports no overflow.
 */
export function useOverflowTabsNoop(_selectedTabIndex: number): ReturnType<typeof useOverflowTabs> {
	const tabListRef = useRef<HTMLDivElement | null>(null);
	const moreButtonRef = useRef<HTMLDivElement | null>(null);
	return {
		tabListRef,
		moreButtonRef,
		hasOverflow: false,
		hiddenTabIndices: EMPTY_SET,
	};
}
